import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useState } from 'react'

import { api } from '~/api/client'
import type { Viewer } from '~/api/types'

/**
 * Who is asking, once, for the whole app.
 *
 * The masthead is mounted by the shell and the shell is above every route, so it cannot get
 * this from the page it is drawing — and it spent the first version of this port hardcoded
 * to `null`, which meant the bar said "Sign in" to somebody already signed in. That exact
 * bug was found and fixed in the Python front page weeks ago; the port reintroduced it, and
 * the Python test that had been guarding it was one of the ones the deploy turned red.
 *
 * `/landing` is the cheapest thing that knows: 3.7 KB and 8ms, public, and the front page
 * needs it anyway. `undefined` means the question has not been answered yet, which is not
 * the same as `null` — drawing "Sign in" during that gap is the same wrong answer, briefly.
 */
/** What is true of the site wherever you are: who is asking, and what the footer says. */
export interface Site {
  me: Viewer | null
  /** When a source last confirmed the listings. Null until the answer arrives. */
  updatedAt: string | null
  handles: string[]
}

const HERE = createContext<Site | undefined>(undefined)

/**
 * Where "look at this as a reader would" is kept.
 *
 * `sessionStorage`, not state and not the server. Not state, because the toggle has to
 * survive a reload — half of what an editor wants to check is what a cold visit looks like.
 * Not the server, because it is not a fact about the account: it is a way of looking at the
 * page, and writing it to `account.is_editor` would mean the curated calendar quietly stops
 * being curated for everybody until it is switched back. And `sessionStorage` rather than
 * `localStorage`, so closing the tab always ends it — an editor who forgets they left it on
 * would otherwise find the star, the fold box and the curate controls gone the next morning
 * with nothing to explain it.
 */
const AS_READER = 'lg:as-reader'

/** Whether the editor is currently pretending not to be one. */
export function readingAsReader(): boolean {
  try {
    return sessionStorage.getItem(AS_READER) === '1'
  } catch {
    // Private browsing throws on `sessionStorage`. Not being able to pretend is a far
    // smaller problem than the app not drawing.
    return false
  }
}

/**
 * Turn it on or off, and optionally land somewhere.
 *
 * A full navigation either way, because `isEditor` is read at every level of the tree and
 * half the components that check it hold their own copy from a fetch that has already run.
 * A router navigation would leave those copies saying the old thing.
 *
 * `to` is for the one door that only exists on the other side of the switch: the briefing
 * bounces anybody who is not curating, so "3 waiting in your briefing" has to turn the
 * switch off and arrive, rather than turn it off and leave you where you were.
 */
export function readAsReader(on: boolean, to?: string): void {
  try {
    sessionStorage.setItem(AS_READER, on ? '1' : '0')
  } catch {
    return
  }
  if (to) window.location.assign(to)
  else window.location.reload()
}

export function ViewerProvider({ children }: { children: ReactNode }) {
  const [site, setSite] = useState<Site | undefined>(undefined)

  useEffect(() => {
    let live = true
    api
      .landing()
      .then(data => {
        // Masked here and nowhere else. Every control the editor has is drawn behind
        // `me.isEditor`, so one lie at the source is the whole feature — anything that
        // enumerated the controls instead would be a list to keep in step with the app.
        //
        // The *server* is not lied to: it still knows who is asking, and would still accept
        // a curate. Nothing can reach it, because nothing that sends one is on the page.
        // The `isEditor` half is belt and braces rather than a condition that changes
        // anything: setting `isEditor: false` on somebody for whom it is already false is
        // the same object by value. It is written out because the alternative reads as
        // "mask whoever is asking", which is not what this does.
        const me =
          data.me && data.me.isEditor && readingAsReader()
            ? { ...data.me, isEditor: false }
            : data.me
        if (live) setSite({ me, updatedAt: data.updatedAt, handles: data.handles })
      })
      // A reader whose session has expired is a reader who is not signed in, and a footer
      // with no stamp is a footer. Nothing here may stop the app drawing.
      .catch(() => live && setSite({ me: null, updatedAt: null, handles: [] }))
    return () => {
      live = false
    }
  }, [])

  return <HERE.Provider value={site}>{children}</HERE.Provider>
}

/** `undefined` until the answer arrives, then the viewer or `null`. */
export function useViewer(): Viewer | null | undefined {
  return useContext(HERE)?.me
}

/** The footer's two facts. Empty until the answer arrives, which reads as a footer with
 *  nothing to say rather than as a missing footer. */
export function useSite(): Site {
  return useContext(HERE) ?? { me: null, updatedAt: null, handles: [] }
}
